import React from 'react';
import styles from '../../../styles/TaskDetails.module.css';
import CountdownTimer from './CountdownTimer';
import TaskDescriptionText from './TaskDescriptionText';
import TaskAssignees from './TaskAssignees';
import TaskApprovers from './TaskApprovers';
import TaskAttachments from './TaskAttachments';

interface TaskCategory {
  id: string;
  name: string;
  color: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  name: string;
  username: string;
  email: string;
  full_name?: string;
}

interface TaskAssignment {
  id: string;
  user: User;
  points_earned: number;
  status: 'pending' | 'in_progress' | 'completed';
  assigned_points: number;
  assigned_percentage: number;
  work_description: string;
  is_team_lead: boolean;
}

interface Approver {
  id: string;
  user: User;
}

interface TaskHeaderProps {
  task: {
    id: string;
    name: string;
    description: string;
    status: string;
    priority: string;
    due_date: string | null;
    category: TaskCategory;
    requires_approval: boolean;
    approval_type?: string;
    requires_documents: boolean;
    is_instance?: boolean;
    is_user_instance?: boolean;
    assignees: TaskAssignment[];
    approvers?: Approver[];
    documents: any[];
  };
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    total: number;
  } | null;
  isPersonalMode?: boolean;
  isTemplate?: boolean;
  currentUserId?: string | null;
}

const TaskHeader: React.FC<TaskHeaderProps> = ({ task, timeRemaining, isPersonalMode = false, isTemplate = false, currentUserId = null }) => {
  const isInstance = task.is_instance || task.is_user_instance;
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
      case 'not_started':
        return '#6b7280';
      case 'in_progress':
        return '#3b82f6';
      case 'completed':
        return '#10b981';
      case 'cancelled':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No deadline set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={styles.currentUserStatusCard}>
      {timeRemaining && (
        <CountdownTimer timeRemaining={timeRemaining} taskStatus={task.status} />
      )}
      <div className={styles.taskCardHeader}>
        <div className={styles.taskCardHeaderContent}>
          <div>
            <div className={styles.taskCardTitle}>{task.name}</div>
          </div>
          {/* Status Badge - Hide for templates */}
          {!(isTemplate && !isInstance) && (
            <div className={styles.taskCardStatusBadge}>
              <span
                className={styles.statusBadgeText}
                style={{ backgroundColor: getStatusColor(task.status) }}
              >
                {task.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <div className={styles.taskCardTags}>
          {/* Category Badge */}
          <div
            className={styles.categoryTag}
            style={{ backgroundColor: task.category.color, color: 'white' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            {task.category.name}
          </div>

          {/* Due Date Badge */}
          <div className={styles.dueDateTag}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {isInstance ? 'Instance Ends at: ' : 'Due: '}{formatDate(task.due_date)}
          </div>

          {/* Priority Badge */}
          <div className={`${styles.priorityTag} ${styles[`priority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`]}`}>
            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
          </div>

          {task.requires_approval && (
            <div className={styles.requirementItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22,4 12,14.01 9,11.01"></polyline>
              </svg>
              <span style={{ color: '#f59e0b' }}>Requires Approval</span>
            </div>
          )}

          {task.requires_documents && (
            <div className={styles.requirementItem}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
              <span style={{ color: '#3b82f6' }}>Requires Documents</span>
            </div>
          )}
        </div>

        <div className={styles.taskCardSubtitle}>
          <TaskDescriptionText description={task.description} />
        </div>
      </div>

      {/* Assignees Section - specific visibility logic */}
      {task.assignees && task.assignees.length > 0 && (() => {
        const isAssignee = currentUserId && task.assignees.some(a => a.user.id === currentUserId);

        const shouldShowAssignees = !((isTemplate || isInstance) && isAssignee);

        const title = isInstance ? 'Submitted By' : 'Assignees';

        return shouldShowAssignees ? (
          <TaskAssignees
            assignees={task.assignees}
            isInstance={isInstance}
            title={title}
          />
        ) : null;
      })()}

      {/* Approvers Section - Show whenever approvers exist */}
      {task.requires_approval && task.approvers && task.approvers.length > 0 && (
        <TaskApprovers approvers={task.approvers} />
      )}

      {/* Attachments Section */}
      <TaskAttachments documents={task.documents} />
    </div>
  );
};

export default TaskHeader;

